import React, { useEffect, useState, useCallback, useMemo } from "react";
import apiService from "@/api-service";
import { Spinner, Row, Col, Table, Container, Form, Button, Dropdown, InputGroup } from "react-bootstrap";
import { PaginationControl } from "react-bootstrap-pagination-control";
import { useNavigate } from "react-router-dom";
import { MdDownload, MdArrowUpward, MdArrowDownward } from "react-icons/md";
import useLoading from "@/hooks/loading.hook";
import useAPIDownload from "@/hooks/download.hook";
import { useDebounce } from "@/hooks/debounce.hook";
import { BepalingLidListItem } from "@/models/bepalingen";
import BepalingFilters from "@/components/bepaling-filters/bepaling-filters";
import FilterInfoButton from "@/components/common/filter-info-button";
import { connect, useDispatch } from "react-redux";
import { RootState } from "@/store";
import { BepalingFilterState, bepalingFilterActions } from "@/store/reducers/bepaling_filter";

interface SortConfig {
    key: 'full_nummer' | 'tekst' | 'vermeldingen' | 'bestekken' | 'versie_jaarversie';
    direction: 'asc' | 'desc';
}

interface SortableHeaderProps {
    children: React.ReactNode;
    sortKey: 'full_nummer' | 'tekst' | 'vermeldingen' | 'bestekken' | 'versie_jaarversie';
    currentSort: SortConfig | null;
    onSort: (key: 'full_nummer' | 'tekst' | 'vermeldingen' | 'bestekken' | 'versie_jaarversie') => void;
    style?: React.CSSProperties;
}

const SortableHeader: React.FC<SortableHeaderProps> = ({ children, sortKey, currentSort, onSort, style }) => {
    const isActive = currentSort?.key === sortKey;
    const direction = isActive ? currentSort.direction : null;

    return (
        <th
            style={{ ...style, cursor: 'pointer', userSelect: 'none', position: 'relative' }}
            onClick={() => onSort(sortKey)}
            className={isActive ? 'table-active' : ''}
        >
            <div className="d-flex align-items-center justify-content-between">
                <span>{children}</span>
                <span style={{ marginLeft: '5px', opacity: isActive ? 1 : 0.3 }}>
                    {direction === 'asc' ? <MdArrowUpward size={14} /> : <MdArrowDownward size={14} />}
                </span>
            </div>
        </th>
    );
};


interface Props {
    bepaling_filter: BepalingFilterState;
}

function mapStateToProps(state: RootState): Props {
    return {
        bepaling_filter: state.bepaling_filter,
    };
}

function BepalingenList({ bepaling_filter }: Props) {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const loader = useLoading();
    const downloadFromAPI = useAPIDownload();

    const beschrijving = bepaling_filter.beschrijving;
    const hoofdstukFilter = bepaling_filter.hoofdstukFilter;
    const paragraafFilter = bepaling_filter.paragraafFilter;
    const artikelFilter = bepaling_filter.artikelFilter;
    const lidFilter = bepaling_filter.lidFilter;
    const jaarversieFilter = bepaling_filter.jaarversieFilter;
    const releaseFilter = bepaling_filter.releaseFilter;
    const sortConfig = bepaling_filter.sortConfig;
    const activePage = bepaling_filter.activePage;

    const [totalCount, setTotalCount] = useState<number>(-1);
    const [bepalingen, setBepalingen] = useState<BepalingLidListItem[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [pageInput, setPageInput] = useState<string>("");
    const [filterVisible, setFilterVisible] = useState<boolean>(true);
    // FIXED: Jaarversie staat nu los van BepalingFilters (zelfde architectuur als de
    // hoofdcodes-lijstpagina), zodat hij als eerste kolom vóór Beschrijving kan staan.
    const [beschikbareJaarversies, setBeschikbareJaarversies] = useState<string[]>([]);
    // NIEUW: "release versie" — cascadeert onder jaarversie, zelfde bron als bestekken/hoofdcodes.
    const [beschikbareReleases, setBeschikbareReleases] = useState<string[]>([]);

    const pageSize: number = 10;
    const debouncedBeschrijving = useDebounce(beschrijving, 500);

    const handleSort = useCallback((key: 'full_nummer' | 'tekst' | 'vermeldingen' | 'bestekken' | 'versie_jaarversie') => {
        const direction: 'asc' | 'desc' =
            sortConfig?.key === key && sortConfig.direction === 'asc' ? 'desc' : 'asc';
        dispatch(bepalingFilterActions.set_sort_config({ key, direction }));
    }, [sortConfig, dispatch]);

    const fetch = useCallback(() => {
        setLoading(true);

        const params: { [key: string]: any } = {
            pagesize: pageSize,
            page: activePage,
            include_usage: true,
            search_text: debouncedBeschrijving
        };

        if (hoofdstukFilter) params['hoofdstuk_nummer'] = hoofdstukFilter;
        if (paragraafFilter) params['paragraaf_nummer'] = paragraafFilter;
        if (artikelFilter) params['artikel_nummer'] = artikelFilter;
        if (lidFilter) params['lid_nummer'] = lidFilter;
        if (jaarversieFilter) params['versie_jaarversie'] = jaarversieFilter;
        if (releaseFilter) params['versie'] = releaseFilter;
        if (sortConfig) {
            params['sort_by'] = sortConfig.key;
            params['sort_direction'] = sortConfig.direction;
        }

        apiService.post<{ result: BepalingLidListItem[], total_count: number }>("/bepaling/list/", params)
            .then((res) => {
                const totalPages = Math.ceil(res.data.total_count / pageSize);
                if (activePage > totalPages && totalPages > 0) {
                    dispatch(bepalingFilterActions.set_active_page(totalPages));
                    return;
                }
                setBepalingen(res.data.result || []);
                setTotalCount(res.data.total_count || 0);
            }, (error) => {
                console.error('API Error:', error);
                setBepalingen([]);
                setTotalCount(0);
            }).finally(() => setLoading(false));
    }, [debouncedBeschrijving, activePage, hoofdstukFilter, paragraafFilter, artikelFilter, lidFilter, jaarversieFilter, releaseFilter, pageSize, sortConfig, dispatch]);

    const handleHoofdstukChange = useCallback((value: string) => {
        dispatch(bepalingFilterActions.set_hoofdstuk_filter(value));
    }, [dispatch]);

    const handleParagraafChange = useCallback((value: string) => {
        dispatch(bepalingFilterActions.set_paragraaf_filter(value));
    }, [dispatch]);

    const handleArtikelChange = useCallback((value: string) => {
        dispatch(bepalingFilterActions.set_artikel_filter(value));
    }, [dispatch]);

    const handleLidChange = useCallback((value: string) => {
        dispatch(bepalingFilterActions.set_lid_filter(value));
    }, [dispatch]);

    const handleJaarversieChange = useCallback((value: string) => {
        dispatch(bepalingFilterActions.set_jaarversie_filter(value));
    }, [dispatch]);

    const handleReleaseChange = useCallback((value: string) => {
        dispatch(bepalingFilterActions.set_release_filter(value));
    }, [dispatch]);

    const getCurrentFilterParams = useCallback(() => {
        const params: any = {
            search_text: debouncedBeschrijving || undefined,
            include_usage: true
        };
        if (hoofdstukFilter) params.hoofdstuk_nummer = hoofdstukFilter;
        if (paragraafFilter) params.paragraaf_nummer = paragraafFilter;
        if (artikelFilter) params.artikel_nummer = artikelFilter;
        if (lidFilter) params.lid_nummer = lidFilter;
        if (jaarversieFilter) params.versie_jaarversie = jaarversieFilter;
        if (releaseFilter) params.versie = releaseFilter;
        if (sortConfig) {
            params.sort_by = sortConfig.key;
            params.sort_direction = sortConfig.direction;
        }
        return params;
    }, [debouncedBeschrijving, hoofdstukFilter, paragraafFilter, artikelFilter, lidFilter, jaarversieFilter, releaseFilter, sortConfig]);

    const download = useCallback((format: 'csv' | 'xlsx'): void => {
        loader.show("Export voorbereiden...");
        downloadFromAPI(
            { method: 'post', path: '/bepaling/export/', data: { search_params: getCurrentFilterParams(), format } },
            `bepalingen_export.${format}`,
            { finally: loader.hide }
        );
    }, [getCurrentFilterParams, loader, downloadFromAPI]);

    const handlePageChange = useCallback((newPage: number) => {
        const totalPages = Math.ceil(totalCount / pageSize);
        const safePage = Math.min(Math.max(1, newPage), Math.max(1, totalPages));
        dispatch(bepalingFilterActions.set_active_page(safePage));
    }, [totalCount, pageSize, dispatch]);

    const totalPages = useMemo(() => Math.ceil(totalCount / pageSize), [totalCount, pageSize]);

    const handleGoToPage = useCallback(() => {
        const pageNum = parseInt(pageInput);
        if (pageNum >= 1 && pageNum <= totalPages) {
            handlePageChange(pageNum);
            setPageInput("");
        }
    }, [pageInput, totalPages, handlePageChange]);

    const handleKeyPressPageInput = useCallback((e: React.KeyboardEvent) => {
        if (e.key === 'Enter') handleGoToPage();
    }, [handleGoToPage]);

    const validActivePage = useMemo(() => {
        if (totalCount <= 0) return 1;
        const totalPages = Math.ceil(totalCount / pageSize);
        return Math.min(Math.max(1, activePage), totalPages);
    }, [totalCount, pageSize, activePage]);

    useEffect(() => {
        apiService.get<string[]>("/bepaling/jaarversies/").then((res) => {
            setBeschikbareJaarversies(res.data);
        });
    }, []);

    // NIEUW: "release versie" opties, cascaderend onder jaarversie — hergebruikt hetzelfde
    // endpoint als bestekken/hoofdcodes.
    useEffect(() => {
        const params = jaarversieFilter ? `?versie_jaarversie=${encodeURIComponent(jaarversieFilter)}` : '';
        apiService.get<string[]>(`/bestek/releases${params}`).then((res) => {
            setBeschikbareReleases(res.data);
        });
    }, [jaarversieFilter]);

    useEffect(() => {
        fetch();
    }, [fetch]);

    const tableRows = useMemo(() => {
        if (loading) {
            return (
                <tr>
                    <td colSpan={5} align="center">
                        <Spinner size="sm" className="me-2" />
                        Laden...
                    </td>
                </tr>
            );
        }

        if (totalCount === 0) {
            return (
                <tr>
                    <td colSpan={5} align="center">
                        Er zijn geen bepalingen die voldoen aan de zoekcriteria
                    </td>
                </tr>
            );
        }

        if (!bepalingen || bepalingen.length === 0) {
            if (totalCount > 0) {
                return (
                    <tr>
                        <td colSpan={5} align="center">
                            Geen resultaten op deze pagina. Navigeer naar een andere pagina.
                        </td>
                    </tr>
                );
            } else {
                return (
                    <tr>
                        <td colSpan={5} align="center">
                            Geen data beschikbaar
                        </td>
                    </tr>
                );
            }
        }

        return bepalingen.map(bepaling => (
            <tr
                key={`bepaling-lid-${bepaling.id}-${bepaling.versie_jaarversie}`}
                onClick={() => navigate(`/bepalingen/lid/${bepaling.id}`)}
                style={{ cursor: 'pointer' }}
            >
                <td>{bepaling.full_nummer}</td>
                <td style={{ width: '110px' }}>{bepaling.versie_jaarversie}</td>
                <td
                    style={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        maxWidth: '1px',
                        paddingRight: '40px'
                    }}
                    title={bepaling.tekst || 'Geen tekst'}
                >
                    {bepaling.tekst || 'Geen tekst'}
                </td>
                <td style={{ width: '160px' }}>{bepaling.bepaling_usage?.vermeldingen || 0}</td>
                <td style={{ width: '160px' }}>{bepaling.bepaling_usage?.bestekken || 0}</td>
            </tr>
        ));
    }, [totalCount, bepalingen, navigate, loading]);

    return (
        <div className="pt-4">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="mb-0 d-flex align-items-center gap-1">
                    Zoekfilters
                    <FilterInfoButton
                        id="bepalingen-filter-uitleg"
                        text="Zoekt in de tekst van bepalingen. De filters werken als trechter van boven naar beneden. Vermeldingen = hoe vaak een bepaling in bestekken is opgenomen. Aantal bestekken = in hoeveel unieke bestekken. Deze aantallen veranderen niet met uw zoekterm."
                    />
                </h5>
                <div className="d-flex align-items-center gap-2">
                    {totalCount >= 0 && (
                        <span className="text-muted" style={{ fontSize: '13px' }}>
                            {totalCount} {totalCount === 1 ? 'resultaat' : 'resultaten'}
                        </span>
                    )}
                    <Dropdown>
                        <Dropdown.Toggle variant="outline-secondary" size="sm">
                            <MdDownload className="me-1" />Export
                        </Dropdown.Toggle>
                        <Dropdown.Menu align="end">
                            {totalCount > 0 && totalCount <= 1000 ? (
                                <>
                                    <Dropdown.Item onClick={() => download('csv')}>CSV</Dropdown.Item>
                                    <Dropdown.Item onClick={() => download('xlsx')}>Excel</Dropdown.Item>
                                </>
                            ) : totalCount > 1000 ? (
                                <Dropdown.ItemText className="small text-muted">
                                    Gebruik aangepaste export voor &gt;1000 resultaten.
                                </Dropdown.ItemText>
                            ) : (
                                <Dropdown.ItemText className="small text-muted">Geen resultaten.</Dropdown.ItemText>
                            )}
                        </Dropdown.Menu>
                    </Dropdown>
                    <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => setFilterVisible(!filterVisible)}
                    >
                        {filterVisible ? 'Filter verbergen' : 'Filter tonen'}
                    </Button>
                </div>
            </div>

            <div>
                {filterVisible && (
                    <Container fluid>
                        {/* FIXED: volgorde nu Jaarversie, Beschrijving, Hoofdstuk, Paragraaf,
                            Artikel, Lid — en breedtes zo verdeeld dat dit op een lg-scherm op
                            1 regel past (2 + 3 + 7 = 12), i.p.v. Jaarversie die naar een eigen
                            regel wegviel omdat hij niet meer paste. */}
                        <Row className="g-3">
                            <Col lg={2} md={3} sm={4}>
                                <label className="form-label mb-1">Jaarversie</label>
                                <Form.Select
                                    size="sm"
                                    value={jaarversieFilter}
                                    onChange={(e) => handleJaarversieChange(e.target.value)}
                                >
                                    <option value="">Alle versies</option>
                                    {beschikbareJaarversies.map((v) => (
                                        <option key={v} value={v}>{v}</option>
                                    ))}
                                </Form.Select>
                            </Col>
                            <Col lg={2} md={3} sm={4}>
                                <label className="form-label mb-1">Release versie</label>
                                <Form.Select
                                    size="sm"
                                    value={releaseFilter}
                                    onChange={(e) => handleReleaseChange(e.target.value)}
                                >
                                    <option value="">Alle releases</option>
                                    {beschikbareReleases.map((v) => (
                                        <option key={v} value={v}>{v}</option>
                                    ))}
                                </Form.Select>
                            </Col>
                            <Col lg={3} md={4} sm={12}>
                                <label className="form-label mb-1">Beschrijving</label>
                                <Form.Control
                                    id="bepalingen-search-box"
                                    size="sm"
                                    value={beschrijving ?? ""}
                                    type="text"
                                    placeholder="Zoek in bepalingtekst..."
                                    onChange={(e: any) => dispatch(bepalingFilterActions.set_beschrijving(e.target.value))}
                                />
                                {beschrijving !== debouncedBeschrijving && (
                                    <small className="text-muted">Zoeken...</small>
                                )}
                            </Col>
                            <Col lg={7} md={5} sm={12}>
                                <BepalingFilters
                                    hoofdstukFilter={hoofdstukFilter}
                                    paragraafFilter={paragraafFilter}
                                    artikelFilter={artikelFilter}
                                    lidFilter={lidFilter}
                                    onHoofdstukChange={handleHoofdstukChange}
                                    onParagraafChange={handleParagraafChange}
                                    onArtikelChange={handleArtikelChange}
                                    onLidChange={handleLidChange}
                                />
                            </Col>
                        </Row>
                    </Container>
                )}
            </div>

            {loading && (
                <div className="mt-4 text-center">
                    <Spinner animation="border" role="status" size="sm" className="me-2">
                        <span className="visually-hidden">Loading...</span>
                    </Spinner>
                    <span>Data ophalen...</span>
                </div>
            )}

            {!loading && (
                <>
                    <Table className="mt-3" size="sm" striped hover style={{ tableLayout: 'fixed' }}>
                        <thead>
                            <tr>
                                <SortableHeader
                                    sortKey="full_nummer"
                                    currentSort={sortConfig}
                                    onSort={handleSort}
                                    style={{ width: '145px' }}
                                >
                                    Bepalingnummer
                                </SortableHeader>
                                <SortableHeader
                                    sortKey="versie_jaarversie"
                                    currentSort={sortConfig}
                                    onSort={handleSort}
                                    style={{ width: '110px' }}
                                >
                                    Jaarversie
                                </SortableHeader>
                                <SortableHeader
                                    sortKey="tekst"
                                    currentSort={sortConfig}
                                    onSort={handleSort}
                                >
                                    Beschrijving
                                </SortableHeader>
                                <SortableHeader
                                    sortKey="vermeldingen"
                                    currentSort={sortConfig}
                                    onSort={handleSort}
                                    style={{ width: '160px' }}
                                >
                                    Vermeldingen
                                </SortableHeader>
                                <SortableHeader
                                    sortKey="bestekken"
                                    currentSort={sortConfig}
                                    onSort={handleSort}
                                    style={{ width: '160px' }}
                                >
                                    Aantal bestekken
                                </SortableHeader>
                            </tr>
                        </thead>
                        <tbody>
                            {tableRows}
                        </tbody>
                    </Table>

                    {totalCount > 0 && (
                        <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mt-3">
                            <div style={{ flex: 1, minWidth: '300px' }}>
                                <PaginationControl
                                    page={validActivePage}
                                    between={4}
                                    total={totalCount}
                                    limit={pageSize}
                                    changePage={handlePageChange}
                                    ellipsis={1}
                                />
                            </div>
                            <div className="d-flex flex-column align-items-end">
                                <InputGroup size="sm" style={{ width: '120px' }}>
                                    <Form.Control
                                        type="number"
                                        placeholder="Pagina"
                                        value={pageInput}
                                        onChange={(e) => setPageInput(e.target.value)}
                                        onKeyPress={handleKeyPressPageInput}
                                        min={1}
                                        max={totalPages}
                                    />
                                    <Button
                                        variant="outline-secondary"
                                        onClick={handleGoToPage}
                                        disabled={!pageInput}
                                    >
                                        Ga
                                    </Button>
                                </InputGroup>
                                <small className="text-muted mt-1">
                                    Pagina {validActivePage} van {totalPages}
                                </small>
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}

export default connect(mapStateToProps)(BepalingenList);